import type { JSX } from 'react'

import {
  AllChannelsIcon,
  CircleCheckBigIcon,
  DropdownMenu,
  IconButton,
  ListFilterIcon,
  Text,
  UserIcon,
} from '@buffer-mono/popcorn'

import type { AssignableChannel } from '../../hooks'
import {
  COLLABORATION_ROLE_LABELS,
  COLLABORATION_ROLES,
} from '../../utils/collaborationRoles'
import type { CollaborationRole } from '../../utils/collaborationRoles'
import {
  TEAM_MEMBERS_STATUS_LABELS,
  TEAM_MEMBERS_STATUSES,
} from '../../utils/teamMembersFilters'
import type {
  TeamMembersFilters,
  TeamMembersStatusFilter,
} from '../../utils/teamMembersFilters'

import styles from './TeamMembersFilterMenu.module.css'

const toggle = <T,>(values: T[], value: T): T[] =>
  values.includes(value)
    ? values.filter((current) => current !== value)
    : [...values, value]

const channelsSummary = (
  channelIds: string[],
  channels: AssignableChannel[],
): string => {
  if (channelIds.length === 0) return 'All Channels'
  if (channelIds.length === 1) {
    const match = channels.find(({ channel }) => channel.id === channelIds[0])
    if (match) return match.channel.name
  }
  return `${channelIds.length} channels`
}

const rolesSummary = (roles: CollaborationRole[]): string => {
  if (roles.length === 0) return 'All Roles'
  if (roles.length === 1) return COLLABORATION_ROLE_LABELS[roles[0]]
  return `${roles.length} roles`
}

const RowValue = ({ children }: { children: string }): JSX.Element => (
  <Text color="subtle" className={styles.value}>
    {children}
  </Text>
)

type TeamMembersFilterMenuProps = {
  filters: TeamMembersFilters
  onFiltersChange: (filters: TeamMembersFilters) => void
  channels: AssignableChannel[]
}

export const TeamMembersFilterMenu = ({
  filters,
  onFiltersChange,
  channels,
}: TeamMembersFilterMenuProps): JSX.Element => (
  <DropdownMenu>
    <DropdownMenu.Trigger>
      <IconButton variant="tertiary" label="Filter table">
        <ListFilterIcon />
      </IconButton>
    </DropdownMenu.Trigger>
    <DropdownMenu.Content align="end" className={styles.content}>
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger>
          <AllChannelsIcon />
          <span className={styles.label}>Channels</span>
          <RowValue>{channelsSummary(filters.channelIds, channels)}</RowValue>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubMenu className={styles.subMenu}>
          {channels.length === 0 ? (
            <DropdownMenu.Item disabled>No channels</DropdownMenu.Item>
          ) : (
            channels.map(({ channel }) => (
              <DropdownMenu.CheckboxItem
                key={channel.id}
                checked={filters.channelIds.includes(channel.id)}
                onCheckedChange={(): void =>
                  onFiltersChange({
                    ...filters,
                    channelIds: toggle(filters.channelIds, channel.id),
                  })
                }
                onSelect={(event): void => event.preventDefault()}
              >
                {channel.name}
              </DropdownMenu.CheckboxItem>
            ))
          )}
        </DropdownMenu.SubMenu>
      </DropdownMenu.Sub>
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger>
          <UserIcon />
          <span className={styles.label}>Roles</span>
          <RowValue>{rolesSummary(filters.roles)}</RowValue>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubMenu>
          {COLLABORATION_ROLES.map((role) => (
            <DropdownMenu.CheckboxItem
              key={role}
              checked={filters.roles.includes(role)}
              onCheckedChange={(): void =>
                onFiltersChange({
                  ...filters,
                  roles: toggle(filters.roles, role),
                })
              }
              onSelect={(event): void => event.preventDefault()}
            >
              {COLLABORATION_ROLE_LABELS[role]}
            </DropdownMenu.CheckboxItem>
          ))}
        </DropdownMenu.SubMenu>
      </DropdownMenu.Sub>
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger>
          <CircleCheckBigIcon />
          <span className={styles.label}>Status</span>
          <RowValue>{TEAM_MEMBERS_STATUS_LABELS[filters.status]}</RowValue>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubMenu>
          <DropdownMenu.RadioGroup
            value={filters.status}
            onChange={(status): void =>
              onFiltersChange({
                ...filters,
                status: status as TeamMembersStatusFilter,
              })
            }
          >
            {TEAM_MEMBERS_STATUSES.map((status) => (
              <DropdownMenu.RadioItem key={status} value={status}>
                {TEAM_MEMBERS_STATUS_LABELS[status]}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.SubMenu>
      </DropdownMenu.Sub>
    </DropdownMenu.Content>
  </DropdownMenu>
)
